import type { TOC } from '@ember/component/template-only';
import Chip from 'tio-ui/components/chip';
import { PhCheckCircle, PhXCircle, PhWarning } from 'ember-phosphor-icons';
import { formatAsDollars } from '../../../../../utils/format';

/**
 * Recommendation Card
 * Displays the main AI recommendation with optional financial summary, calculation details,
 * and — for deferred/failed recommendations — why the AI could not decide
 */

export interface DisagreementPoint {
  topic: string;
  summary: string;
  amount_impact?: number | null;
  models?: string[];
}

// `(string & {})` (not `| string`) keeps the named literals visible to
// consumers — a plain `| string` member widens the whole union back to
// `string`, silently discarding them
export type DeferReasonCode =
  | 'no_consensus'
  | 'context_error'
  | 'single_model_amount'
  | 'internal_error'
  | 'unreadable_documents'
  | (string & {});

export interface DeferReasonDetails {
  confidence_level?: number;
  confidence_threshold?: number;
  model_amounts?: Record<string, number>;
  model_weights?: Record<string, number>;
  winning_amount?: number | null;
  winning_model?: string | null;
  models_queried?: number;
  models_responded?: number;
  skipped_documents?: { count: number; names?: string[] };
  superseded_code?: DeferReasonCode;
  error?: string;
  disagreement_points?: DisagreementPoint[];
}

export interface DeferReason {
  schema_version: number;
  code: DeferReasonCode;
  message: string;
  generated_by: 'deterministic' | (string & {});
  details?: DeferReasonDetails;
}

export interface RecommendationCardSignature {
  Args: {
    recommendation: string;
    calculationDetails?: string;
    approvedAmount?: number;
    requestedAmount?: number;
    deferReason?: DeferReason;
  };
}

/**
 * Format recommendation enum value to human-readable text
 */
function formatRecommendation(rec: string): string {
  const formattedMap: Record<string, string> = {
    approved_for_requested_amount: 'Approved for Requested Amount',
    approved_for_less_than_requested: 'Approved for Less Than Requested',
    approved_for_more_than_requested: 'Approved for More Than Requested',
    denied: 'Denied',
    defer_to_human: 'Needs Human Review',
    failure: 'Analysis Failed',
    send_back_for_more_info: 'Sent Back for More Info',
  };

  return formattedMap[rec] || rec.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function modelAmountEntries(deferReason: DeferReason) {
  const amounts = deferReason.details?.model_amounts ?? {};
  return Object.entries(amounts).map(([model, amount]) => ({ model, amount }));
}

function hasModelAmounts(deferReason: DeferReason) {
  return modelAmountEntries(deferReason).length > 0;
}

function confidenceSummary(deferReason: DeferReason) {
  const details = deferReason.details;
  if (typeof details?.confidence_level !== 'number') return undefined;
  const base = `${details.confidence_level} / 5`;
  return typeof details.confidence_threshold === 'number'
    ? `${base} (needs ${details.confidence_threshold})`
    : base;
}

function disagreementPoints(deferReason: DeferReason) {
  return deferReason.details?.disagreement_points ?? [];
}

function hasDisagreementPoints(deferReason: DeferReason) {
  return disagreementPoints(deferReason).length > 0;
}

function isApproved(rec: string): boolean {
  return (
    rec === 'approved_for_requested_amount' ||
    rec === 'approved_for_less_than_requested' ||
    rec === 'approved_for_more_than_requested'
  );
}

function isDenied(rec: string): boolean {
  return rec === 'denied';
}

function hasAmount(value: number | undefined | null): boolean {
  return value !== undefined && value !== null;
}

function safeDollars(value: number | undefined | null, options = { cents: true }): string {
  return formatAsDollars(value ?? 0, options);
}

const RecommendationCard: TOC<RecommendationCardSignature> = <template>
  <div class="tio-bg-brand-soft border tio-border-brand-soft rounded-lg p-4">
    <div class="tio-row-top mb-3">
      {{#if (isApproved @recommendation)}}
        <PhCheckCircle @size="24" class="tio-fg-success shrink-0 mt-0.5" />
        <div class="flex-1">
          <Chip @intent="success" @size="sm">{{formatRecommendation @recommendation}}</Chip>
        </div>
      {{else if (isDenied @recommendation)}}
        <PhXCircle @size="24" class="tio-fg-danger shrink-0 mt-0.5" />
        <div class="flex-1">
          <Chip @intent="danger" @size="sm">{{formatRecommendation @recommendation}}</Chip>
        </div>
      {{else}}
        <PhWarning @size="24" class="tio-fg-warning shrink-0 mt-0.5" />
        <div class="flex-1">
          <Chip @intent="warning" @size="sm">{{formatRecommendation @recommendation}}</Chip>
        </div>
      {{/if}}
    </div>

    {{#if @deferReason}}
      {{! Deferred/failed: the approved amount and calculation details are just
          the winning model's output without consensus behind it — showing them
          would lend it false authority. Requested amount + the why-callout only. }}
      {{#if (hasAmount @requestedAmount)}}
        <div class="mt-3 pt-3 border-t tio-border-brand-soft">
          <div class="tio-row-snug text-sm">
            <span class="tio-fg-secondary">Requested:</span>
            <span class="font-semibold tio-fg-primary">{{safeDollars
                @requestedAmount
                (hash cents=false)
              }}</span>
          </div>
        </div>
      {{/if}}
    {{else}}
      {{! Financial Summary }}
      {{#if (hasAmount @approvedAmount)}}
        <div class="mt-3 pt-3 border-t tio-border-brand-soft">
          <div class="tio-row-between text-sm">
            {{#if @requestedAmount}}
              <div class="tio-row-snug">
                <span class="tio-fg-secondary">Requested:</span>
                <span class="font-semibold tio-fg-primary">{{safeDollars
                    @requestedAmount
                    (hash cents=false)
                  }}</span>
              </div>
            {{/if}}
            <div class="tio-row-snug">
              <span class="tio-fg-secondary">Approved:</span>
              <span class="font-semibold tio-fg-success-body">{{safeDollars
                  @approvedAmount
                  (hash cents=false)
                }}</span>
            </div>
          </div>
        </div>
      {{/if}}

      {{! Calculation Details }}
      {{#if @calculationDetails}}
        <div class="mt-3 pt-3 border-t tio-border-brand-soft">
          <p class="tio-text-sm-semibold tio-fg-secondary mb-1">Calculation Details:</p>
          <p class="text-sm tio-fg-secondary leading-relaxed">{{@calculationDetails}}</p>
        </div>
      {{/if}}
    {{/if}}

    {{! Why this needs human review }}
    {{#if @deferReason}}
      <div
        class="mt-3 pt-3 border-t tio-border-brand-soft"
        data-test-defer-reason
        data-test-defer-reason-code={{@deferReason.code}}
      >
        <div class="tio-row-tight mb-1">
          <PhWarning @size="16" class="tio-fg-warning shrink-0" />
          <p class="tio-text-sm-semibold tio-fg-secondary">Why this needs human review:</p>
        </div>
        <p
          class="text-sm tio-fg-secondary leading-relaxed"
          data-test-defer-reason-message
        >{{@deferReason.message}}</p>

        {{#if (confidenceSummary @deferReason)}}
          <div class="tio-row-between text-sm mt-2">
            <span class="tio-fg-secondary">Consensus confidence:</span>
            <span
              class="font-semibold tio-fg-primary"
              data-test-defer-reason-confidence
            >{{confidenceSummary @deferReason}}</span>
          </div>
        {{/if}}

        {{#if (hasModelAmounts @deferReason)}}
          <div class="mt-2">
            <p class="tio-text-caption mb-1">Amounts by AI reviewer:</p>
            <div class="space-y-1">
              {{#each (modelAmountEntries @deferReason) as |entry|}}
                <div
                  class="tio-row-between text-sm"
                  data-test-defer-reason-model-amount={{entry.model}}
                >
                  <span class="tio-fg-secondary">{{entry.model}}</span>
                  <span class="font-semibold tio-fg-primary">{{safeDollars
                      entry.amount
                      (hash cents=false)
                    }}</span>
                </div>
              {{/each}}
            </div>
          </div>
        {{/if}}

        {{#if (hasDisagreementPoints @deferReason)}}
          <div class="mt-2" data-test-defer-reason-disagreements>
            <p class="tio-text-caption mb-1">What the AI reviewers disagreed about:</p>
            <ul class="text-sm tio-fg-secondary leading-relaxed list-disc pl-5 space-y-1">
              {{#each (disagreementPoints @deferReason) as |point|}}
                <li>{{point.summary}}</li>
              {{/each}}
            </ul>
          </div>
        {{/if}}
      </div>
    {{/if}}
  </div>
</template>;

export default RecommendationCard;
